// ./src/utils/api/ProfitabilityReportService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { CurrencyAmount } from "@/types";
import {
  ExpenseSummaryByCategory,
  ExpenseSummaryByKind,
} from "./ExpenseSummaryReportService";
import { TripTag } from "./TravelReportService";

// ============================================
// GraphQL Query
// ============================================

const PROFITABILITY_REPORT_QUERY = gql`
  query ReportProfitability($filter: ProfitabilityReportFilter!) {
    reportProfitability(filter: $filter) {
      code
      data {
        # Period info
        dateFrom
        dateTo
        periodDays

        # Summary KPIs (Home Currency)
        totalRevenueHc
        totalRevenueCount
        totalRefuelsCostHc
        totalMaintenanceCostHc
        totalOtherExpensesCostHc
        totalExpensesHc
        totalExpensesCount
        netProfitHc
        profitMarginPct
        avgDailyRevenueHc
        avgDailyExpensesHc
        avgDailyNetProfitHc
        totalDistance
        profitPerDistance

        # Foreign currency totals
        foreignRevenueTotals {
          currency
          amount
          recordsCount
        }
        foreignExpenseTotals {
          currency
          amount
          recordsCount
        }
        totalForeignRevenueRecordsCount
        totalForeignExpenseRecordsCount

        # Revenue breakdown
        revenueByCategory {
          categoryId
          categoryCode
          categoryName
          totalAmountHc
          recordsCount
          percentageOfTotal
          foreignCurrencies {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount
        }

        revenueByKind {
          kindId
          kindCode
          kindName
          categoryId
          categoryCode
          totalAmountHc
          recordsCount
          percentageOfTotal
          foreignCurrencies {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount
        }

        # Expense breakdown
        expensesByCategory {
          categoryId
          categoryCode
          categoryName
          totalAmountHc
          recordsCountHc
          percentageHc
          foreignCurrencies {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount
        }

        expensesByKind {
          kindId
          kindCode
          kindName
          categoryId
          categoryCode
          totalAmountHc
          recordsCountHc
          percentageHc
          foreignCurrencies {
            currency
            amount
            recordsCount
          }
          totalForeignRecordsCount
        }

        # Per-vehicle profitability
        byVehicle {
          carId
          revenueHc
          revenueCount
          refuelsCostHc
          maintenanceCostHc
          otherExpensesCostHc
          totalExpensesHc
          expensesCount
          netProfitHc
          profitMarginPct
          distance
          profitPerDistance
          revenuePerDistance
          expensesPerDistance
        }

        # Monthly trend
        monthlyTrend {
          month
          year
          revenueHc
          revenueCount
          refuelsCostHc
          maintenanceCostHc
          otherExpensesCostHc
          totalExpensesHc
          expensesCount
          netProfitHc
          distance
          profitPerDistance
        }

        # Per-trip profitability
        profitableTrips {
          tripId
          carId
          date
          purpose
          destination
          travelType
          distance
          revenueHc
          revenueCount
          linkedRefuelsHc
          linkedExpensesHc
          totalLinkedExpensesHc
          netProfitHc
          profitPerDistance
          tags {
            id
            tagName
            tagColor
          }
        }

        profitableTripsTotals {
          totalTrips
          totalDistance
          totalRevenueHc
          totalLinkedRefuelsHc
          totalLinkedExpensesHc
          totalLinkedAllExpensesHc
          totalNetProfitHc
        }

        # Break-even analysis
        breakEven {
          avgDailyRevenueHc
          avgDailyExpensesHc
          avgDailyNetProfitHc
          daysToBreakEven
          breakEvenDayInPeriod
          isProfitable
        }

        # Odometer warnings
        odometerWarnings {
          carId
          totalRecords
          recordsWithOdometer
          recordsMissingOdometer
          missingPercentage
          largestGapDistance
          warningLevel
        }

        # User preferences
        distanceUnit
        volumeUnit
        homeCurrency
        vehiclesCount
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// ============================================
// Types
// ============================================

/**
 * Filter input for profitability report
 */
export interface ProfitabilityReportFilter {
  /** Filter by car IDs (optional - all cars if not specified) */
  carId?: string[] | null;
  /** Filter by tag IDs */
  tagId?: string[] | null;
  /** Start date (YYYY-MM-DD format, UTC) - required */
  dateFrom: string;
  /** End date (YYYY-MM-DD format, UTC) - required */
  dateTo: string;
}

/**
 * Revenue breakdown by category
 */
export interface RevenueCategoryBreakdown {
  /** Category ID */
  categoryId?: number;
  /** Category code */
  categoryCode?: string;
  /** Localized category name */
  categoryName?: string;
  /** Total amount in home currency */
  totalAmountHc?: number;
  /** Number of records */
  recordsCount?: number;
  /** Percentage of total revenue */
  percentageOfTotal?: number;
  /** Foreign currency breakdowns */
  foreignCurrencies?: CurrencyAmount[];
  /** Total records in foreign currencies */
  totalForeignRecordsCount?: number;
}

/**
 * Revenue breakdown by kind (sub-category)
 */
export interface RevenueKindBreakdown {
  /** Kind ID */
  kindId?: number;
  /** Kind code */
  kindCode?: string;
  /** Localized kind name */
  kindName?: string;
  /** Parent category ID */
  categoryId?: number;
  /** Parent category code */
  categoryCode?: string;
  /** Total amount in home currency */
  totalAmountHc?: number;
  /** Number of records */
  recordsCount?: number;
  /** Percentage of total revenue */
  percentageOfTotal?: number;
  /** Foreign currency breakdowns */
  foreignCurrencies?: CurrencyAmount[];
  /** Total records in foreign currencies */
  totalForeignRecordsCount?: number;
}

/**
 * Profitability metrics for a single vehicle
 */
export interface VehicleProfitability {
  /** Vehicle ID */
  carId?: string;
  /** Total revenue in home currency */
  revenueHc?: number;
  /** Number of revenue records */
  revenueCount?: number;
  /** Refuels cost in home currency */
  refuelsCostHc?: number;
  /** Maintenance cost in home currency */
  maintenanceCostHc?: number;
  /** Other expenses cost in home currency */
  otherExpensesCostHc?: number;
  /** Total expenses in home currency */
  totalExpensesHc?: number;
  /** Number of expense records */
  expensesCount?: number;
  /** Net profit (revenue - expenses) */
  netProfitHc?: number;
  /** Profit margin percentage */
  profitMarginPct?: number | null;
  /** Distance driven in user's preferred unit */
  distance?: number | null;
  /** Net profit per distance unit */
  profitPerDistance?: number | null;
  /** Revenue per distance unit */
  revenuePerDistance?: number | null;
  /** Expenses per distance unit */
  expensesPerDistance?: number | null;
}

/**
 * Monthly profitability data point for trend charts
 */
export interface ProfitabilityMonthlyTrend {
  /** Month number (1-12) */
  month?: number;
  /** Year */
  year?: number;
  /** Revenue in home currency */
  revenueHc?: number;
  /** Number of revenue records */
  revenueCount?: number;
  /** Refuels cost in home currency */
  refuelsCostHc?: number;
  /** Maintenance cost in home currency */
  maintenanceCostHc?: number;
  /** Other expenses cost in home currency */
  otherExpensesCostHc?: number;
  /** Total expenses in home currency */
  totalExpensesHc?: number;
  /** Number of expense records */
  expensesCount?: number;
  /** Net profit for the month */
  netProfitHc?: number;
  /** Distance driven in this month (user's unit) */
  distance?: number | null;
  /** Profit per distance unit for this month */
  profitPerDistance?: number | null;
}

/**
 * Profitability for a single trip with linked revenue
 */
export interface TripProfitability {
  /** Trip ID */
  tripId?: string;
  /** Vehicle ID */
  carId?: string;
  /** Trip date */
  date?: string;
  /** Trip purpose */
  purpose?: string;
  /** Trip destination */
  destination?: string;
  /** Travel type (business/personal) */
  travelType?: string;
  /** Trip distance in user's unit */
  distance?: number | null;
  /** Revenue linked to this trip */
  revenueHc?: number;
  /** Number of revenue records */
  revenueCount?: number;
  /** Linked refuels cost */
  linkedRefuelsHc?: number;
  /** Linked expenses cost */
  linkedExpensesHc?: number;
  /** Total linked expenses (refuels + expenses) */
  totalLinkedExpensesHc?: number;
  /** Trip net profit */
  netProfitHc?: number;
  /** Profit per distance unit */
  profitPerDistance?: number | null;
  /** Trip tags */
  tags?: TripTag[];
}

/**
 * Totals for the per-trip profitability table
 */
export interface TripProfitabilityTotals {
  /** Total number of trips */
  totalTrips?: number;
  /** Total distance across all trips */
  totalDistance?: number | null;
  /** Total revenue across all trips */
  totalRevenueHc?: number;
  /** Total linked refuels cost */
  totalLinkedRefuelsHc?: number;
  /** Total linked expenses cost */
  totalLinkedExpensesHc?: number;
  /** Total linked expenses (refuels + expenses) */
  totalLinkedAllExpensesHc?: number;
  /** Total net profit */
  totalNetProfitHc?: number;
}

/**
 * Break-even analysis for the period
 */
export interface BreakEvenAnalysis {
  /** Average daily revenue in home currency */
  avgDailyRevenueHc?: number;
  /** Average daily expenses in home currency */
  avgDailyExpensesHc?: number;
  /** Average daily net profit */
  avgDailyNetProfitHc?: number;
  /** Estimated days to break even (null if already profitable) */
  daysToBreakEven?: number | null;
  /** Day number in period when break-even was reached (null if never) */
  breakEvenDayInPeriod?: number | null;
  /** Whether the period is overall profitable */
  isProfitable?: boolean;
}

/**
 * Odometer data quality warning for a vehicle
 */
export interface OdometerWarning {
  /** Vehicle ID */
  carId?: string;
  /** Total records in period */
  totalRecords?: number;
  /** Records with odometer readings */
  recordsWithOdometer?: number;
  /** Records missing odometer */
  recordsMissingOdometer?: number;
  /** Percentage of records missing odometer (0-100) */
  missingPercentage?: number;
  /** Largest gap between consecutive readings (user's distance unit) */
  largestGapDistance?: number | null;
  /** Warning level: 'none' | 'low' | 'high' */
  warningLevel?: string;
}

/**
 * Complete profitability report data
 */
export interface ProfitabilityReport {
  // Period info
  /** Report start date */
  dateFrom?: string;
  /** Report end date */
  dateTo?: string;
  /** Number of days in the period */
  periodDays?: number;

  // Summary KPIs (Home Currency)
  /** Total revenue across all selected vehicles */
  totalRevenueHc?: number;
  /** Total revenue record count */
  totalRevenueCount?: number;
  /** Total refuels cost in home currency */
  totalRefuelsCostHc?: number;
  /** Total maintenance cost in home currency */
  totalMaintenanceCostHc?: number;
  /** Total other expenses cost in home currency */
  totalOtherExpensesCostHc?: number;
  /** Total expenses (refuels + maintenance + other) */
  totalExpensesHc?: number;
  /** Total expense record count */
  totalExpensesCount?: number;
  /** Net profit (totalRevenue - totalExpenses) */
  netProfitHc?: number;
  /** Profit margin: (netProfit / totalRevenue) × 100 */
  profitMarginPct?: number | null;
  /** Average daily revenue */
  avgDailyRevenueHc?: number;
  /** Average daily expenses */
  avgDailyExpensesHc?: number;
  /** Average daily net profit */
  avgDailyNetProfitHc?: number;
  /** Total distance driven in period (user's unit) */
  totalDistance?: number | null;
  /** Net profit per distance unit */
  profitPerDistance?: number | null;

  // Foreign currency totals
  /** Revenue totals by foreign currency */
  foreignRevenueTotals?: CurrencyAmount[];
  /** Expense totals by foreign currency */
  foreignExpenseTotals?: CurrencyAmount[];
  /** Total revenue records in foreign currencies */
  totalForeignRevenueRecordsCount?: number;
  /** Total expense records in foreign currencies */
  totalForeignExpenseRecordsCount?: number;

  // Revenue breakdowns
  /** Revenue grouped by category */
  revenueByCategory?: RevenueCategoryBreakdown[];
  /** Revenue grouped by kind */
  revenueByKind?: RevenueKindBreakdown[];

  // Expense breakdowns (reuses existing pattern)
  /** Expenses grouped by category */
  expensesByCategory?: ExpenseSummaryByCategory[];
  /** Expenses grouped by kind */
  expensesByKind?: ExpenseSummaryByKind[];

  // Per-vehicle profitability
  /** Profitability metrics for each vehicle */
  byVehicle?: VehicleProfitability[];

  // Monthly trend
  /** Monthly revenue/expense/profit trend data */
  monthlyTrend?: ProfitabilityMonthlyTrend[];

  // Per-trip profitability
  /** Trips that have linked revenue — shows per-trip profit */
  profitableTrips?: TripProfitability[];
  /** Totals for the per-trip profitability table */
  profitableTripsTotals?: TripProfitabilityTotals;

  // Break-even analysis
  /** Break-even analysis for the period */
  breakEven?: BreakEvenAnalysis;

  /** Odometer data quality warnings per vehicle */
  odometerWarnings?: OdometerWarning[];

  // User preferences (for display formatting)
  /** User's distance unit (km/mi) */
  distanceUnit?: string;
  /** User's volume unit (L/gal) */
  volumeUnit?: string;
  /** User's home currency code */
  homeCurrency?: string;
  /** Number of vehicles in the report */
  vehiclesCount?: number;
}

// ============================================
// Service Class
// ============================================

/**
 * ProfitabilityReportService handles API calls for the profitability report.
 *
 * This report provides vehicle revenue vs expenses analysis designed for
 * gig workers, delivery drivers, and small fleet operators to understand
 * whether their vehicles are making or losing money.
 *
 * Key metrics include:
 * - Net profit and profit margin
 * - Profit per km/mile
 * - Per-vehicle and per-trip profitability
 * - Monthly trend analysis
 * - Break-even analysis
 * - Revenue and expense breakdowns by category/kind
 */
export class ProfitabilityReportService {
  /**
   * Get profitability report for the specified period.
   *
   * @param filter - Required filters with dateFrom and dateTo
   * @returns OpResult containing ProfitabilityReport data
   *
   * @example
   * // Get report for all cars, last quarter
   * const result = await ProfitabilityReportService.get({
   *   dateFrom: '2025-10-01',
   *   dateTo: '2025-12-31'
   * });
   *
   * @example
   * // Get report for specific vehicles with tags
   * const result = await ProfitabilityReportService.get({
   *   carId: ['car-uuid-123', 'car-uuid-456'],
   *   tagId: ['tag-business'],
   *   dateFrom: '2025-01-01',
   *   dateTo: '2025-12-31'
   * });
   *
   * @example
   * // Access per-vehicle profitability
   * const result = await ProfitabilityReportService.get({
   *   dateFrom: '2025-01-01',
   *   dateTo: '2025-12-31'
   * });
   * const report = result.data?.[0];
   * report?.byVehicle?.forEach(v => {
   *   console.log(`Vehicle ${v.carId}: profit $${v.netProfitHc}, margin ${v.profitMarginPct}%`);
   * });
   */
  static async get(filter: ProfitabilityReportFilter): Promise<OpResult> {
    const result = await ApiService.executeQuery(PROFITABILITY_REPORT_QUERY, {
      filter,
    });
    return result;
  }
}